import { Control } from "Controls/Control";
import { DisplayModes } from "Enums";
import { ControlTemplate } from "Controls/Entities";
import { EventArgs } from "Core/Events";
import { IDataControl } from "Controls/Interfaces";
import { IODataResult } from "Entities";
import { ControlEventTypes } from "Core/Events/EventTypes";
import { Util, Mapping, PageContext } from "Core";
import { MappingDisplayTypes } from "Service/Client";

/**
 * Control displays an empty div container with a fixed id.
 * The control can be initialized with custom implementation in DMS Script.
 * @since 4.0.0
 * @example
 * ```JavaScript
 * // Access the instance of an Custom with ControlID 1337
 * // In script:
 * @@ctr:1337@@
 *
 * // On page:
 * $dms(1337)
 * ```
 */
export class Custom extends Control implements IDataControl {
    private htmlId = "dms-custom-" + this.controlDefinition.ControlID;

    private height: number;

    /**
     * @hidden
     */
    protected dmsControlClass: string = "dms-control-custom";

    /**
     * Internal method for setElement.
     * @param element JQueryElement for initialization.
     * @hidden
     */
    protected setElementInternal(element: JQuery): void {
        if (this.height) {
            element.height(this.height);
        }

        if (this.controlDefinition.Script && this.controlDefinition.CommandID == null) {
            let script = Util.resolve(this.controlDefinition.Script, {});
            let eventArgs = new EventArgs(this);
            this.eventHandler.executeScript(script, eventArgs);
        }
    }

    /**
     * Internal method for getting the template.
     * @param displayMode DisplayMode for control.
     * @param bindingField Field which the control is bound to.
     * @hidden
     */
    protected getTemplateInternal(displayMode: DisplayModes, bindingField?: string): ControlTemplate {
        let controlTemplate = new ControlTemplate();
        let elem = $("<div>");
        elem.prop("id", <any>this.htmlId);
        this.setDefaultEditorProperties(elem);
        controlTemplate.template = elem[0].outerHTML;
        controlTemplate.element = elem;
        return controlTemplate;
    }

    /**
     * Gets the html id of the control.
     * @since 4.0.0
     */
    public getId(): string {
        return this.htmlId;
    }

    /**
     * Gets value of control
     * @hidden
     */
    public getValue(): string {
        return this.jQueryElement.val() as string;
    }

    /**
     * Sets value of control
     * @hidden
     */
    public setValue(value: string): void {
        let oldValue = this.getValue();
        if (oldValue !== value) {
            this.jQueryElement.val(value);
            this.jQueryElement.change();
        }
    }

    /**
     * Show loading indicator on the control.
     * @param isBusy True if loading indicator should be shown.
     * @since 4.0.0
     */
    public setBusy(isBusy: boolean): void {
        kendo.ui.progress(this.jQueryElement, isBusy);
    }

    private data: IODataResult;

    /**
     * Set the odata-result to the control.
     * @param data Data to be set.
     * @hidden
     */
    public setData(data: IODataResult): void {
        this.data = data;
        let script = Util.resolve(this.controlDefinition.Script, {});
        let eventArgs = new EventArgs(this, data);

        this.eventHandler.executeScript(script, eventArgs);
    }

    /**
     * Reload the control items with data from the server.
     * @since 4.0.0
     */
    public reload(): Promise<void> {
        return this.execute<Promise<void>>(ControlEventTypes.ControlReloadRequested);
    }

    private mapping: Mapping;

    /**
     * Initialize a mapping in this control instance.
     * @param mappingName Name of the mapping.
     * @param displayType Display type for the mapping.
     * @since 4.0.0
     * @example
     * ```JavaScript
     * @@ctr:1337@@.initializeMapping("int_TextBlock");
     * @@ctr:1337@@.initializeMapping("int_TextBlock", DMS.Enums.MappingDisplayTypes.GridsOnly);
     * ```
     */
    public async initializeMapping(
        mappingName: string,
        displayType: MappingDisplayTypes = MappingDisplayTypes.FilterAndGrids
    ): Promise<void> {
        this.disposeMapping();
        this.mapping = await PageContext.initializeMappingInContainer(this.jQueryElement, mappingName, displayType);
        if (this.height) {
            this.mapping.setHeight(this.height);
        }
    }

    public getMapping(): Mapping {
        return this.mapping;
    }

    public disposeMapping(clearDom: boolean = false): void {
        if (this.mapping) {
            PageContext.removeAdditionalMapping(this.mapping);
            this.mapping.dispose();
            if (clearDom) {
                this.jQueryElement.empty();
            }
            this.mapping = undefined;
        }
    }

    /**
     * Set the height for the control.
     * If the CustomControl contains a mapping, the mapping will be resized.
     * Otherwise the initialization script will be executed again.
     * @param height Height in Pixel.
     */
    public setHeight(height: number) {
        this.height = height;

        if (this.jQueryElement) {
            this.jQueryElement.height(height);

            if (this.mapping) {
                this.mapping.setHeight(height);
            } else if (this.controlDefinition.Script) {
                let script = Util.resolve(this.controlDefinition.Script, {});
                let eventArgs = new EventArgs(this, this.data);
                this.eventHandler.executeScript(script, eventArgs);
            }
        }
    }

    /**
     * Dispose the control and release all resources.
     * @since 4.0.0
     */
    public dispose(): void {
        super.dispose();
        this.disposeMapping();
    }
}
